import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { openAuthenticatedFile } from '../../api/client.js';
import type { AdminQuoteListEntry, Paginated } from '../../api/types.js';
import { statusText } from '../../components/QuoteView.js';
import { Alert, PageHeader, datum, eur, fehlertext, useLoad } from '../shared/ui.js';

/**
 * Zentrale Angebotsuebersicht fuer Betreiber und Vertrieb.
 *
 * Archiv- und Nachschlagewerkzeug: **lesen, oeffnen, PDF** -- sonst nichts. Hier
 * gibt es keinen Knopf, der ein Angebot aendert, neu berechnet, loescht oder
 * revidiert; ausgestellte Belege sind archivierte Dokumente.
 *
 * Alle Daten kommen aus einer Abfrage (`/api/admin/quotes`): Kunde und Ersteller
 * stehen bereits am Eintrag, es wird nichts je Zeile nachgeladen. Die Kundenauswahl
 * im Filter nutzt dieselbe Kundenliste wie die Kundenseite.
 */
const SEITENGROESSE = 25;

const STATUS_FILTER = [
  ['', 'alle Status'],
  ['draft', 'Entwurf'],
  ['issued', 'ausgestellt'],
  ['expired', 'abgelaufen'],
  ['withdrawn', 'zurueckgezogen'],
] as const;

export function QuotesPage(): React.JSX.Element {
  const navigate = useNavigate();
  /** Eingabe im Suchfeld; wird erst beim Absenden zum wirksamen Filter. */
  const [q, setQ] = useState('');
  const [suche, setSuche] = useState('');
  const [kunde, setKunde] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);

  const params = new URLSearchParams({
    limit: String(SEITENGROESSE),
    offset: String(offset),
  });
  if (suche !== '') params.set('q', suche);
  if (kunde !== '') params.set('accountId', kunde);
  if (status !== '') params.set('status', status);

  const { data, error, loading } = useLoad<Paginated<AdminQuoteListEntry>>(
    `/api/admin/quotes?${params}`,
  );
  const konten = useLoad<{
    items: Array<{ accountId: string; name: string; accountNumber: string }>;
  }>('/api/admin/accounts?limit=200&sort=name');

  /** Jede Filteraenderung beginnt wieder auf der ersten Seite. */
  function setzeFilter(aenderung: () => void): void {
    aenderung();
    setOffset(0);
  }

  const total = data?.total ?? 0;
  const von = total === 0 ? 0 : offset + 1;
  const bis = Math.min(offset + SEITENGROESSE, total);

  return (
    <>
      <PageHeader
        title="Angebote"
        lead="Alle Angebote aller Kunden. Ausgestellte Angebote sind archivierte Belege und werden hier nur gelesen."
      />
      <form
        className="filterbar"
        onSubmit={(e) => {
          e.preventDefault();
          setzeFilter(() => setSuche(q.trim()));
        }}
      >
        <input
          type="search"
          placeholder="Angebotsnummer"
          aria-label="Angebotsnummer"
          maxLength={40}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit">Suchen</button>
        <select
          value={kunde}
          onChange={(e) => setzeFilter(() => setKunde(e.target.value))}
          aria-label="Kunde"
        >
          <option value="">alle Kunden</option>
          {(konten.data?.items ?? []).map((k) => (
            <option key={k.accountId} value={k.accountId}>
              {k.name} ({k.accountNumber})
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setzeFilter(() => setStatus(e.target.value))}
          aria-label="Status"
        >
          {STATUS_FILTER.map(([wert, text]) => (
            <option key={wert} value={wert}>
              {text}
            </option>
          ))}
        </select>
      </form>
      <Alert text={error} />
      <Alert text={fehler} />
      {data !== null && (
        <div className="card">
          <p className="note">
            {total === 0
              ? 'Keine Angebote fuer diese Auswahl.'
              : `${von}–${bis} von ${total} Angeboten`}
          </p>
          {data.items.length > 0 && (
            <div className="table-scroll">
              <table className="clickable">
                <thead>
                  <tr>
                    <th>Angebotsnummer</th>
                    <th>Kunde</th>
                    <th>Datum</th>
                    <th>Status</th>
                    <th className="num">Gesamt netto</th>
                    <th>Waehrung</th>
                    <th>Ausgestellt</th>
                    <th>Ersteller</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => (
                    <tr
                      key={a.quoteId}
                      onClick={() => navigate(`/admin/angebote/${a.quoteId}`)}
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && navigate(`/admin/angebote/${a.quoteId}`)
                      }
                    >
                      <td>
                        {a.quoteNumber === null ? (
                          <span className="muted">– (Entwurf)</span>
                        ) : (
                          <code>{a.quoteNumber}</code>
                        )}
                      </td>
                      <td>
                        <strong>{a.accountName}</strong>{' '}
                        <span className="muted">({a.accountNumber})</span>
                      </td>
                      <td>{datum(a.quoteDate ?? a.createdAt)}</td>
                      <td>{statusText(a.status)}</td>
                      <td className="num">{eur(a.documentTotalNet)}</td>
                      <td>{a.currency}</td>
                      <td>{datum(a.issuedAt)}</td>
                      <td>{a.createdByName}</td>
                      <td>
                        {/* Knoepfe stoppen die Zeilennavigation, damit ein Klick nicht beides ausloest. */}
                        <div className="row" onClick={(e) => e.stopPropagation()}>
                          <Link to={`/admin/angebote/${a.quoteId}`}>Details</Link>
                          {a.pdfAvailable ? (
                            <button
                              type="button"
                              className="link"
                              onClick={() => {
                                setFehler(null);
                                void openAuthenticatedFile(
                                  `/api/quotes/${a.quoteId}/pdf`,
                                ).catch((e: unknown) => setFehler(fehlertext(e)));
                              }}
                            >
                              PDF
                            </button>
                          ) : (
                            <span className="muted">kein PDF</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row pager">
            <button
              type="button"
              disabled={loading || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - SEITENGROESSE))}
            >
              Zurueck
            </button>
            <button
              type="button"
              disabled={loading || bis >= total}
              onClick={() => setOffset(offset + SEITENGROESSE)}
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </>
  );
}
